import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadMixtape, togglePlaying } from 'store/playerSlice';
import { loadMixById } from 'mixtape';

const fileName = (src, title) => {
  if (title) return title;
  try {
    const path = decodeURIComponent(src.split('?')[0]);
    return path.split('/').pop().replace(/\.[^.]+$/, '');
  } catch {
    return '';
  }
};

// <Play src="https://..." title="Track Name" /> — a centered button that
// hands the url to the shared navbar player. Crossfades if something's playing.
// <Play mix="02" /> instead loads a full mixtape from the archive.
const Play = ({ src, title, mix }) => {
  const { scheme } = useSelector((s) => s.session);
  const { mixtape, isPlaying } = useSelector((s) => s.player);
  const dispatch = useDispatch();

  const isThis = mixtape && (mix ? mixtape.id === mix : mixtape.src === src);
  const playing = isThis && isPlaying;
  const label = mix
    ? title || (isThis && mixtape.title) || `Mix ${mix}`
    : fileName(src, title);

  const load = () => {
    if (mix) loadMixById(dispatch, mix, { crossfade: true });
    else dispatch(loadMixtape({ title: label, src, tracks: [], crossfade: true }));
  };

  const onClick = (e) => {
    e.stopPropagation();
    e.currentTarget.blur();
    if (isThis) dispatch(togglePlaying());
    else load();
  };

  // When this inline player is crossed (mounts as the story types past it) and
  // something else is already playing, auto-crossfade into this track.
  useEffect(() => {
    if (isPlaying && !isThis) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const icon = playing ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill={scheme.text}>
      <rect x="6" y="5" width="4" height="14" />
      <rect x="14" y="5" width="4" height="14" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill={scheme.text}>
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  return (
    <button
      className="play-box"
      onClick={onClick}
      title={`${playing ? 'Pause' : 'Play'} ${label}`.trim()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.6rem',
        margin: '1.5rem auto',
        padding: '1.2rem',
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: '0.6rem',
        background: 'transparent',
        color: scheme.text,
        font: 'inherit'
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '3.4rem',
          height: '3.4rem',
          borderRadius: '50%',
          border: `2px solid ${scheme.text}`,
          background: 'rgba(0,0,0,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {icon}
      </span>
      {label && (
        <span
          style={{
            fontFamily:
              "'Berkeley','AAMonoCJK','MS Gothic','Osaka-Mono','Courier New',Courier,monospace",
            fontSize: '0.85rem'
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
};

export default Play;
